/**
 * SCADA Write Service - API communication for SCADA write operations
 */

import {
  SCADAWriteRequest, SCADAWriteResponse, SCADAWriteLogEntry,
  WriteSessionRequest, WriteSessionResponse, WriteSessionStatus,
  TOTPSetupRequest, TOTPSetupResponse, TOTPVerificationRequest,
  TOTPVerificationResponse, TOTPDevice
} from '../types/scada';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Global CSRF token cache
let csrfToken: string | null = null;

/**
 * Get CSRF token from backend
 */
const getCSRFToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.warn('Failed to get CSRF token:', error);
  }
  
  return '';
};

/**
 * Generic API request helper
 */
const apiRequest = async (endpoint: string, options?: RequestInit) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}/${endpoint}`;
  
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const scadaWriteAPI = {
  // SCADA Write Operations
  writeSetpoint: async (tagId: number, writeRequest: SCADAWriteRequest): Promise<SCADAWriteResponse> => {
    const endpoint = `sites/scada-tags/${tagId}/write_setpoint/`;
    const token = await getCSRFToken();
    
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      credentials: 'include',
      body: JSON.stringify(writeRequest),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to write setpoint`);
    }

    return response.json();
  },

  getWriteLogs: async (tagId?: number, limit: number = 50): Promise<SCADAWriteLogEntry[]> => {
    let endpoint = 'scada-write-logs/';
    const params = new URLSearchParams();
    
    if (tagId) {
      params.append('tag', tagId.toString());
    }
    params.append('limit', limit.toString());
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    return apiRequest(endpoint);
  },

  getWriteLog: async (logId: number): Promise<SCADAWriteLogEntry> => {
    const endpoint = `scada-write-logs/${logId}/`;
    return apiRequest(endpoint);
  },

  // Write Session Management
  getWriteSessionStatus: async (siteId: number): Promise<WriteSessionStatus> => {
    const endpoint = `sites/scada-write/session/status/${siteId}/`;
    return apiRequest(endpoint);
  },

  startWriteSession: async (request: WriteSessionRequest): Promise<WriteSessionResponse> => {
    const endpoint = 'sites/scada-write/session/start/';
    const token = await getCSRFToken();
    
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to start write session`);
    }

    return response.json();
  },

  endWriteSession: async (siteId: number): Promise<{ success: boolean; message?: string }> => {
    const endpoint = 'sites/scada-write/session/end/';
    const token = await getCSRFToken();
    
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      credentials: 'include',
      body: JSON.stringify({ site_id: siteId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to end write session`);
    }

    return response.json();
  },

  // TOTP Management
  setupTOTP: async (request: TOTPSetupRequest): Promise<TOTPSetupResponse> => {
    const endpoint = 'sites/totp/setup/';
    const token = await getCSRFToken();
    
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to setup TOTP`);
    }

    return response.json();
  },

  verifyTOTP: async (request: TOTPVerificationRequest): Promise<TOTPVerificationResponse> => {
    const endpoint = 'sites/totp/verify/';
    const token = await getCSRFToken();
    
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to verify TOTP`);
    }

    return response.json();
  },

  getTOTPDevice: async (): Promise<TOTPDevice | null> => {
    try {
      const endpoint = 'sites/totp/device/';
      return await apiRequest(endpoint);
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null; // No TOTP device configured
      }
      throw error;
    }
  },
};
